/* eslint-disable react-hooks/rules-of-hooks */
/**
 * API Store
 * Manages API configuration state including dynamic project selection
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

const SUPPORTED_API_LANGUAGES = ["en", "es", "fr", "ar"];

const normalizeApiLanguage = (language) => {
  if (!language || typeof language !== "string") {
    return "en";
  }

  const normalized = language.trim().toLowerCase();
  if (normalized.startsWith("es")) {
    return "es";
  }

  return SUPPORTED_API_LANGUAGES.includes(normalized) ? normalized : "en";
};

// Default API configuration
const DEFAULT_API_CONFIG = {
  project: "orderingv1",
  api: {
    url: "https://apiv4.ordering.co",
    version: "v400",
    language: "en",
  },
};

// Initial state
const initialApiState = {
  project: DEFAULT_API_CONFIG.project,
  api: DEFAULT_API_CONFIG.api,
  isConfigured: false,
};

/**
 * API Store
 * Handles API configuration state and actions
 */
export const useApiStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialApiState,

        // Set the project for API calls
        setProject: (project) => {
          if (!project || typeof project !== "string") {
            throw new Error("Project name is required and must be a string");
          }

          set({
            project: project.trim(),
            isConfigured: true,
          });
        },

        // Set API language from UI locale mapping
        setApiLanguage: (language) => {
          const normalizedLanguage = normalizeApiLanguage(language);

          set((state) => {
            if (state.api.language === normalizedLanguage) {
              return state;
            }

            return {
              api: {
                ...state.api,
                language: normalizedLanguage,
              },
            };
          });
        },

        // Set complete API configuration
        setApiConfig: (config) => {
          const { project, api } = config;

          if (!project || typeof project !== "string") {
            throw new Error("Project name is required and must be a string");
          }

          set({
            project: project.trim(),
            api: {
              ...DEFAULT_API_CONFIG.api,
              ...api,
              ...(api?.language
                ? { language: normalizeApiLanguage(api.language) }
                : {}),
            },
            isConfigured: true,
          });
        },

        // Get the base URL for API calls
        getBaseUrl: () => {
          const state = get();
          const { url, version, language } = state.api;
          const project = state.project;

          return `${url.replace(/\/$/, "")}/${version}/${language}/${project}`;
        },

        // Get current project
        getCurrentProject: () => {
          return get().project;
        },

        // Check if API is configured
        isApiConfigured: () => {
          return get().isConfigured;
        },

        // Reset to default configuration
        resetToDefault: () => {
          set({
            ...initialApiState,
            isConfigured: false,
          });
        },

        // Clear configuration
        clearConfig: () => {
          set({
            ...initialApiState,
            isConfigured: false,
          });
        },
      }),
      {
        name: "api-config-storage",
        // Only persist the essential configuration
        partialize: (state) => ({
          project: state.project,
          api: state.api,
          isConfigured: state.isConfigured,
        }),
      },
    ),
    {
      name: "api-store",
    },
  ),
);

// Selectors for easier access
export const getCurrentProject = () => useApiStore((state) => state.project);
export const setProject = (project) =>
  useApiStore((state) => state.setProject(project));
export const setApiLanguage = (language) =>
  useApiStore((state) => state.setApiLanguage(language));
export const getApiConfig = () => useApiStore((state) => state.api);
export const isApiConfigured = () => useApiStore((state) => state.isConfigured);
export const getBaseUrl = () => useApiStore((state) => state.getBaseUrl());
